import {
  AutoRedemptionSettingStrategy,
  AutoRedemptionSettingType,
  PeriodAutoRedemptionSettingStrategyOptions,
} from './auto-redemption-setting.type';

export enum CashBackAutoRedemptionType {
  CashBackToCard,
  CashBackToAccount,
}

interface BaseAutoRedemptionSettingModelAttributes {
  id: string;
  userId: string;
  pointsAccountId: string;
  pointsRequired: number;
  strategy: AutoRedemptionSettingStrategy;
  // when AutoRedemptionSettingStrategy is every few months, the object must not be empty
  strategyOptions: PeriodAutoRedemptionSettingStrategyOptions | {};
  //  new attributes
  redeemAt: string | null;
  description: string;
  productSku: string;
}

export interface DigitalGiftCardAutoRedemptionSettingModelAttributes
  extends BaseAutoRedemptionSettingModelAttributes {
  data: {
    minimumTransfer: number;
    productName: string;
    productImageUrl: string;
    thresholdAmount: null | number;
    giftCardId: string;
    recipient: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      message: string;
    };
    displayValue: number;
  };
}

interface DigitalGiftCardAutoRedemptionSettingModel {
  id: string;
  type: AutoRedemptionSettingType.DigitalGiftCard;
  attributes: DigitalGiftCardAutoRedemptionSettingModelAttributes;
}

export interface PhysicalGiftCardAutoRedemptionSettingModelAttributes
  extends BaseAutoRedemptionSettingModelAttributes {
  data: {
    productName: string;
    productImageUrl: string;
    thresholdAmount: null | number;
    minimumTransfer: number;
    giftCardId: string;
    recipient: {
      firstName: string;
      lastName: string;
      email: string;
      message: string;
      companyName: string;
      phoneNumber: string;
      address: {
        line1: string;
        line2: string;
        city: string;
        countryCode: string;
        postalCode: string;
        state: string;
      };
    };
    displayValue: number;
  };
}

interface PhysicalGiftCardAutoRedemptionSettingModel {
  id: string;
  type: AutoRedemptionSettingType.PhysicalGiftCard;
  attributes: PhysicalGiftCardAutoRedemptionSettingModelAttributes;
}

export interface CashAutoRedemptionSettingModelAttributes<
  T extends CashBackAutoRedemptionType,
> extends BaseAutoRedemptionSettingModelAttributes {
  data: {
    details: T extends CashBackAutoRedemptionType.CashBackToCard
      ? {
          groupName: string;
          productCode: string;
        }
      : {
          bsbNumber: string;
        };
    productName: string;
    productImageUrl: string;
    minimumTransfer: number;
    thresholdAmount: null | number;
    membershipId: string;
    number: string;
    firstName: string;
    lastName: string;
    enrollmentId: T extends CashBackAutoRedemptionType.CashBackToCard
      ? string
      : never;
    loyaltyProgramId: string;
  };
}

interface CashRedemptionAutoRedemptionSettingModel<
  T extends CashBackAutoRedemptionType,
> {
  id: string;
  type: AutoRedemptionSettingType.Cash;
  attributes: CashAutoRedemptionSettingModelAttributes<T>;
}

export interface PointsTransferAutoRedemptionSettingModelAttributes
  extends BaseAutoRedemptionSettingModelAttributes {
  data: {
    firstName: string;
    lastName: string;
    productName: string;
    productCategory: 'retail' | (string & {});
    productImageUrl: string;
    thresholdAmount: null | number;
    minimumTransfer: number;
    membershipId: string;
    number: string;
    loyaltyProgramId: string;
  };
}

interface PointsTransferAutoRedemptionSettingModel {
  id: string;
  type: AutoRedemptionSettingType.PointsTransfer;
  attributes: PointsTransferAutoRedemptionSettingModelAttributes;
}

export type AutoRedemptionSettingModel =
  | DigitalGiftCardAutoRedemptionSettingModel
  | PhysicalGiftCardAutoRedemptionSettingModel
  | CashRedemptionAutoRedemptionSettingModel<CashBackAutoRedemptionType>
  | PointsTransferAutoRedemptionSettingModel;
